import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingForm from '../components/BookingForm';
import FeaturedRoutePromotion from '../components/FeaturedRoutePromotion';
import SeoHead from '../components/SeoHead';
import { CUSTOMER_SUPPORT } from '../data/customerPolicies';
import {
  VEHICLES,
  getVehicleDisplayName,
} from '../data/bookingConfig';
import {
  CBE_OOTY_AIRPORT_PICKUP,
  CBE_OOTY_DESTINATION,
  CBE_OOTY_FAQS,
  CBE_OOTY_ORIGIN,
  CBE_OOTY_PATH,
  CBE_OOTY_REVERSE_PATH,
  CBE_OOTY_SEO,
  CBE_OOTY_WHATSAPP_MESSAGE,
  OOTY_DESTINATION_IMAGES,
  OOTY_PACKAGES,
  OOTY_PHOTO_CREDITS,
  OOTY_SIGHTSEEING_GROUPS,
} from '../data/coimbatoreToOotyPage';
import { OOTY_ONE_WAY_FARES } from '../data/popularRoutes';
import '../styles/CoimbatoreToOoty.css';

const BOOKING_ID = 'route-booking';
const HERO_IMAGE = OOTY_DESTINATION_IMAGES.hero.src;

const getRouteMetrics = async (origin, destination) => {
  try {
    if (!window.google) return null;
    const service = new window.google.maps.DistanceMatrixService();
    const response = await service.getDistanceMatrix({
      origins: [origin],
      destinations: [destination],
      travelMode: window.google.maps.TravelMode.DRIVING,
      unitSystem: window.google.maps.UnitSystem.METRIC,
    });
    const element = response.rows?.[0]?.elements?.[0];
    if (element?.status === 'OK' && element.distance) {
      return {
        distanceKm: Math.round((element.distance.value / 1000) * 10) / 10,
        durationText: element.duration?.text || null,
      };
    }
  } catch (error) {
    console.error('Route metrics error:', error);
  }
  return null;
};

const whatsappHref = (message = CBE_OOTY_WHATSAPP_MESSAGE) => {
  const digits = CUSTOMER_SUPPORT.phoneTel.replace(/\D/g, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
};

const formatRupee = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

const packageEnquiryHref = (pkg) => {
  const fareLine = pkg.prices
    .map((price) => `${price.label} ${formatRupee(price.amount)}`)
    .join(', ');
  return whatsappHref(
    `Hello, I want the ${pkg.title} Ooty sightseeing cab. Listed fares: ${fareLine}. Please confirm the vehicle and availability. This is separate from a Coimbatore to Ooty taxi.`,
  );
};

const CoimbatoreToOoty = () => {
  const [preferredVehicle, setPreferredVehicle] = useState('');
  const [tripType, setTripType] = useState('One Way');
  const [pickupPrefill, setPickupPrefill] = useState(CBE_OOTY_ORIGIN);
  const [dropPrefill, setDropPrefill] = useState(CBE_OOTY_DESTINATION);
  const [routeMetrics, setRouteMetrics] = useState(null);
  const bookingRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadMetrics = async () => {
      const metrics = await getRouteMetrics(CBE_OOTY_ORIGIN, CBE_OOTY_DESTINATION);
      if (!cancelled && metrics) setRouteMetrics(metrics);
    };

    if (window.google) {
      loadMetrics();
    } else {
      window.addEventListener('google-maps-ready', loadMetrics, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('google-maps-ready', loadMetrics);
    };
  }, []);

  const scrollToBooking = useCallback(() => {
    const target = document.getElementById(BOOKING_ID);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => {
        const focusable = target.querySelector('input, button, select, textarea');
        if (focusable && typeof focusable.focus === 'function') {
          focusable.focus({ preventScroll: true });
        }
      }, 400);
    }
  }, []);

  const applyTripMode = (mode) => {
    if (mode === 'Airport Transfer') {
      setTripType('One Way');
      setPickupPrefill(CBE_OOTY_AIRPORT_PICKUP);
    } else {
      setTripType(mode);
      setPickupPrefill(CBE_OOTY_ORIGIN);
    }
    window.setTimeout(scrollToBooking, 50);
  };

  const handleSelectVehicle = (vehicleType) => {
    setPreferredVehicle(vehicleType);
    window.setTimeout(scrollToBooking, 50);
  };

  const formKey = `${pickupPrefill}|${dropPrefill}|${tripType}|${preferredVehicle}`;

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://easydroptaxi.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Coimbatore to Ooty Taxi Service',
            item: `https://easydroptaxi.com${CBE_OOTY_PATH}`,
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Coimbatore to Ooty Taxi Service',
        serviceType: 'One-way taxi booking',
        provider: {
          '@type': 'TaxiService',
          name: 'Easy Drop Taxi',
          url: 'https://easydroptaxi.com/',
          telephone: CUSTOMER_SUPPORT.phoneTel.replace('tel:', ''),
        },
        areaServed: ['Coimbatore', 'Ooty', 'Nilgiris', 'Tamil Nadu'],
        url: `https://easydroptaxi.com${CBE_OOTY_PATH}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: CBE_OOTY_FAQS.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
    [],
  );

  const activeChip =
    pickupPrefill === CBE_OOTY_AIRPORT_PICKUP ? 'Airport Transfer' : tripType;

  return (
    <main className="cbe-ooty">
      <SeoHead
        title={CBE_OOTY_SEO.title}
        description={CBE_OOTY_SEO.description}
        path={CBE_OOTY_PATH}
        image={CBE_OOTY_SEO.image}
        jsonLd={jsonLd}
      />

      <section className="cbe-hero" aria-label="Coimbatore to Ooty taxi booking">
        <div className="cbe-hero__media" aria-hidden="true">
          <img
            src={HERO_IMAGE}
            alt=""
            width={1600}
            height={900}
            fetchPriority="high"
            decoding="async"
          />
        </div>
        <div className="cbe-hero__overlay" aria-hidden="true" />

        <div className="cbe-hero__inner">
          <div>
            <nav className="cbe-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <Link to="/taxi-routes">Taxi Routes</Link>
              <span aria-hidden="true">/</span>
              <span>Coimbatore to Ooty</span>
            </nav>

            <h1>{CBE_OOTY_SEO.h1}</h1>
            <p className="cbe-hero__lead">{CBE_OOTY_SEO.heroLead}</p>

            <div className="cbe-trip-chips" role="group" aria-label="Trip type shortcuts">
              <button
                type="button"
                className={`cbe-chip${activeChip === 'One Way' ? ' is-active' : ''}`}
                aria-pressed={activeChip === 'One Way'}
                onClick={() => applyTripMode('One Way')}
              >
                One Way Drop
              </button>
              <button
                type="button"
                className={`cbe-chip${activeChip === 'Round Trip' ? ' is-active' : ''}`}
                aria-pressed={activeChip === 'Round Trip'}
                onClick={() => applyTripMode('Round Trip')}
              >
                Round Trip
              </button>
              <button
                type="button"
                className={`cbe-chip${activeChip === 'Airport Transfer' ? ' is-active' : ''}`}
                aria-pressed={activeChip === 'Airport Transfer'}
                onClick={() => applyTripMode('Airport Transfer')}
                title="Prefills pickup as Coimbatore Airport"
              >
                Airport Transfer
              </button>
            </div>

            <div className="cbe-hero__actions">
              <button type="button" className="cbe-btn cbe-btn--gold" onClick={scrollToBooking}>
                Get Fare Estimate
              </button>
              <a className="cbe-btn cbe-btn--ghost" href={CUSTOMER_SUPPORT.phoneTel}>
                <i className="fa fa-phone" aria-hidden="true" />
                Call EasyDropTaxi
              </a>
            </div>
          </div>

          <div id={BOOKING_ID} ref={bookingRef} className="cbe-booking-card">
            <p className="cbe-booking-card__title">Book Coimbatore → Ooty</p>
            <BookingForm
              key={formKey}
              initialPickup={pickupPrefill}
              initialDrop={dropPrefill}
              initialTripType={tripType}
              initialVehicle={preferredVehicle}
            />
          </div>
        </div>
      </section>

      <FeaturedRoutePromotion
        origin={CBE_OOTY_ORIGIN}
        destination={CBE_OOTY_DESTINATION}
        onGetFareEstimate={({ origin, destination, vehicleType }) => {
          setPickupPrefill(origin);
          setDropPrefill(destination);
          setPreferredVehicle(vehicleType);
          scrollToBooking();
        }}
      />

      <section className="cbe-highlights" aria-label="Route highlights">
        <div className="cbe-container">
          <div className="cbe-highlights__grid">
            <article className="cbe-highlight-card">
              <span className="cbe-highlight-card__label">Route</span>
              <span className="cbe-highlight-card__value">Coimbatore to Ooty</span>
            </article>
            <article className="cbe-highlight-card">
              <span className="cbe-highlight-card__label">Est. distance</span>
              <span className="cbe-highlight-card__value">
                {routeMetrics?.distanceKm
                  ? `${routeMetrics.distanceKm} km (live map)`
                  : 'Shown after fare estimate'}
              </span>
            </article>
            <article className="cbe-highlight-card">
              <span className="cbe-highlight-card__label">Est. duration</span>
              <span className="cbe-highlight-card__value">
                {routeMetrics?.durationText || 'Calculated for your pickup'}
              </span>
            </article>
            <article className="cbe-highlight-card">
              <span className="cbe-highlight-card__label">Trip options</span>
              <span className="cbe-highlight-card__value">One Way · Round Trip · Sedan / SUV / Crysta</span>
            </article>
          </div>
        </div>
      </section>

      <section className="cbe-section" aria-labelledby="cbe-fare-heading">
        <div className="cbe-container">
          <h2 id="cbe-fare-heading">One-way taxi fare</h2>
          <p className="cbe-section__intro">
            The one-way fare is the same from Coimbatore to Ooty and from Ooty to Coimbatore. Tolls and parking are included. Round-trip fares are calculated in the booking form and are not these amounts.
          </p>
          <div className="cbe-fare-grid">
            {VEHICLES.map((vehicle) => (
              <article key={vehicle.type} className="cbe-fare-card">
                <h3>{getVehicleDisplayName(vehicle)}</h3>
                <p className="cbe-fare-card__price">{formatRupee(OOTY_ONE_WAY_FARES[vehicle.type])}</p>
                <p>{vehicle.seats}</p>
                <button
                  type="button"
                  className="cbe-btn cbe-btn--gold"
                  onClick={() => handleSelectVehicle(vehicle.type)}
                >
                  {preferredVehicle === vehicle.type ? 'Selected — Get Fare Estimate' : 'Get Fare Estimate'}
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cbe-section cbe-section--mist" aria-labelledby="cbe-sight-heading">
        <div className="cbe-container">
          <h2 id="cbe-sight-heading">Ooty sightseeing highlights</h2>
          <p className="cbe-section__intro">
            Places covered by the local sightseeing cabs. These stops are not included in the Coimbatore to Ooty taxi fare.
          </p>
          {OOTY_SIGHTSEEING_GROUPS.map((group) => (
            <div key={group.id} className="cbe-sight-group">
              <h3>{group.title}</h3>
              <div className={`cbe-gallery-grid${group.places.length > 1 ? ' cbe-gallery-grid--multi' : ''}`}>
                {group.places.map((place) => (
                  <article key={place.id} className="cbe-gallery-card">
                    <div className="cbe-gallery-card__media">
                      <img
                        src={place.image}
                        alt={place.alt}
                        width={640}
                        height={360}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="cbe-gallery-card__body">
                      <h3>{place.name}</h3>
                      <p>{place.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="cbe-section" aria-labelledby="cbe-packages-heading">
        <div className="cbe-container">
          <h2 id="cbe-packages-heading">Ooty sightseeing cab plans</h2>
          <p className="cbe-section__intro">
            Private cab plans that start and end in Ooty. Choose a duration, then enquire. The taxi from Coimbatore is booked separately.
          </p>
          <div className="cbe-package-grid">
            {OOTY_PACKAGES.map((pkg) => (
              <article key={pkg.id} className="cbe-package-card">
                <div className="cbe-package-card__media">
                  <img
                    src={pkg.image}
                    alt={pkg.alt}
                    width={640}
                    height={360}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="cbe-package-card__body">
                  <p className="cbe-package-card__duration">{pkg.duration}</p>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.summary}</p>
                  <p><strong>Places covered: </strong>{pkg.places}</p>
                  <ul className="cbe-price-list">
                    {pkg.prices.map((price) => (
                      <li key={price.label}>
                        <span>{price.label}</span>
                        <strong>{formatRupee(price.amount)}</strong>
                      </li>
                    ))}
                  </ul>
                  {pkg.vehicleNote ? <p className="cbe-section__note">{pkg.vehicleNote}</p> : null}
                  <details className="cbe-package-details">
                    <summary>Itinerary, inclusions, and exclusions</summary>
                    {pkg.days.map((day) => (
                      <div key={day.label}>
                        <h4>{day.label}</h4>
                        <ul>
                          {day.stops.map((stop) => (
                            <li key={stop}>{stop}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <h4>Included</h4>
                    <ul>
                      {pkg.includes.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    <h4>Not included</h4>
                    <ul>
                      {pkg.excludes.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </details>
                  <a className="cbe-btn cbe-btn--gold" href={packageEnquiryHref(pkg)} target="_blank" rel="noopener noreferrer">
                    Enquire about this plan
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cbe-section cbe-section--mist" aria-labelledby="cbe-plan-heading">
        <div className="cbe-container">
          <h2 id="cbe-plan-heading">How the taxi and the sightseeing cab work</h2>
          <div className="cbe-prose">
            <h3>Taxi from Coimbatore</h3>
            <p>
              One Way leaves you in Ooty. Round Trip keeps the same vehicle for the return. Edit the pickup and drop in the booking card before you estimate, and allow extra time for hill traffic and weather.
            </p>
            <h3>Sightseeing cab in Ooty</h3>
            <p>
              Each plan is a private cab with a driver, fuel, and a set list of stops. Pickup and drop are in Ooty: a hotel, the bus stand, or the railway station, as noted on the card. A day is about eight hours of sightseeing.
            </p>
            <h3>What is not in the sightseeing fare</h3>
            <ul>
              <li>The Coimbatore to Ooty taxi fare</li>
              <li>Hotel rooms and meals</li>
              <li>Entry tickets, toy train tickets, and forest permits</li>
            </ul>
            <h3>How to book</h3>
            <p>
              Use Get Fare Estimate for the taxi. Use Enquire on a package card, or call {CUSTOMER_SUPPORT.phoneDisplay}, to confirm a sightseeing cab. The team confirms the vehicle and the date before the trip.
            </p>
          </div>
        </div>
      </section>

      <section className="cbe-section cbe-section--mist" aria-labelledby="cbe-why-heading">
        <div className="cbe-container">
          <h2 id="cbe-why-heading">Why choose EasyDropTaxi</h2>
          <p className="cbe-section__intro">
            The same booking path used across EasyDropTaxi, with a clear split between the hill taxi and a local sightseeing cab.
          </p>
          <div className="cbe-benefit-grid">
            <article className="cbe-benefit-card">
              <i className="fa fa-laptop" aria-hidden="true" />
              <h3>Easy taxi booking</h3>
              <p>Enter pickup, drop, and your mobile number in the booking card, then get a fare estimate.</p>
            </article>
            <article className="cbe-benefit-card">
              <i className="fa fa-car" aria-hidden="true" />
              <h3>Sedan, SUV, and Crysta</h3>
              <p>Choose the vehicle in the form. The one-way taxi fare for each category is shown above.</p>
            </article>
            <article className="cbe-benefit-card">
              <i className="fa fa-exchange" aria-hidden="true" />
              <h3>One way or round trip</h3>
              <p>Stay on in Ooty with a one-way drop, or keep the same cab for the return.</p>
            </article>
            <article className="cbe-benefit-card">
              <i className="fa fa-map-marker" aria-hidden="true" />
              <h3>Sightseeing by the day</h3>
              <p>One to six day cab plans list the places, the hours, and what is not included.</p>
            </article>
            <article className="cbe-benefit-card">
              <i className="fa fa-calculator" aria-hidden="true" />
              <h3>Separate, stated fares</h3>
              <p>The taxi fare and the sightseeing cab fare are shown as different amounts, so one is not added into the other.</p>
            </article>
            <article className="cbe-benefit-card">
              <i className="fa fa-phone" aria-hidden="true" />
              <h3>Call or WhatsApp</h3>
              <p>
                Reach the team at{' '}
                <a href={CUSTOMER_SUPPORT.phoneTel}>{CUSTOMER_SUPPORT.phoneDisplay}</a>
                {' '}
                to confirm a taxi or a sightseeing cab.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="cbe-cta-band" aria-label="Book Coimbatore to Ooty taxi">
        <div className="cbe-container">
          <h2>Book a Coimbatore to Ooty taxi</h2>
          <p>Get the taxi fare in the booking card, or ask about a sightseeing cab by call or WhatsApp.</p>
          <div className="cbe-cta-band__actions">
            <button type="button" className="cbe-btn cbe-btn--gold" onClick={scrollToBooking}>
              Get Fare Estimate
            </button>
            <button type="button" className="cbe-btn cbe-btn--ghost" onClick={() => applyTripMode('One Way')}>
              Book One-Way Taxi
            </button>
            <button type="button" className="cbe-btn cbe-btn--ghost" onClick={() => applyTripMode('Round Trip')}>
              Book Round-Trip Taxi
            </button>
            <a className="cbe-btn cbe-btn--ghost" href={CUSTOMER_SUPPORT.phoneTel}>
              Call EasyDropTaxi
            </a>
            <a className="cbe-btn cbe-btn--ghost" href={whatsappHref()} target="_blank" rel="noopener noreferrer">
              WhatsApp Enquiry
            </a>
          </div>
        </div>
      </section>

      <section className="cbe-section" aria-labelledby="cbe-faq-heading">
        <div className="cbe-container">
          <h2 id="cbe-faq-heading">Questions about the taxi and sightseeing</h2>
          <p className="cbe-section__intro">
            Answers follow the booking form on this page and the sightseeing plans listed above.
          </p>
          <div className="cbe-faq-list">
            {CBE_OOTY_FAQS.map((item) => (
              <details key={item.question} className="cbe-faq-item">
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
          <nav className="cbe-related" aria-label="Related pages">
            <Link to={CBE_OOTY_REVERSE_PATH}>Ooty to Coimbatore Taxi</Link>
            <Link to="/taxi-routes">All taxi routes</Link>
            <Link to="/blog/coimbatore-to-ooty-drop-taxi">Coimbatore to Ooty travel tips</Link>
            <Link to="/coimbatore-taxi">Coimbatore taxi hub</Link>
          </nav>
          <div className="cbe-photo-credits">
            <h3>Photograph credits</h3>
            <p>
              Destination photographs are from Wikimedia Commons and are not owned by EasyDropTaxi.
            </p>
            <ul>
              {OOTY_PHOTO_CREDITS.map((photo) => (
                <li key={photo.sourceHref}>
                  <a href={photo.sourceHref} target="_blank" rel="noopener noreferrer">{photo.alt}</a>
                  {' — '}
                  {photo.author}
                  {', '}
                  <a href={photo.licenseHref} target="_blank" rel="noopener noreferrer">{photo.license}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CoimbatoreToOoty;
